import type { Map as MapboxMap, Popup } from "mapbox-gl";
import { BoundsRect, IMapInstance, IMapMarker, LatLng, MarkerOptions, RouteLineMode } from "../map-provider.interface";
import { failIfRouteUnansweredAsync } from "../route-timeout";
import { MapboxMarker } from "./mapbox-marker";
import type { MapboxModule } from "./mapbox-provider";

const ROUTE_SOURCE_ID = "route";
const ROUTE_LAYER_ID = "route-line";

// Mapbox draws 512px tiles, so it shows at zoom z what Google shows at z + 1. Callers pass Google-tuned zooms.
export function toMapboxZoom(zoom: number): number {
    return zoom - 1;
}

export function fromMapboxZoom(zoom: number): number {
    return zoom + 1;
}

export class MapboxMapInstance implements IMapInstance {
    private readonly _mapboxgl: MapboxModule;
    private readonly _map: MapboxMap;
    private readonly _apiKey: string;
    private readonly _popup: Popup;
    private readonly _styleLoaded: Promise<void>;

    private _markers: MapboxMarker[] = [];
    private _popupAnchor: IMapMarker | null = null;
    private _routeVersion = 0;
    private _routeAbortController: AbortController | null = null;
    private _idleHandler: (() => void) | null = null;
    private _boundsHandler: (() => void) | null = null;

    constructor(mapboxgl: MapboxModule, map: MapboxMap, apiKey: string, styleLoaded: Promise<void>) {
        this._mapboxgl = mapboxgl;
        this._map = map;
        this._apiKey = apiKey;
        this._popup = new mapboxgl.Popup({ offset: 30, maxWidth: "none" });
        this._styleLoaded = styleLoaded;
    }

    async addMarkerAsync(coords: LatLng, options?: MarkerOptions): Promise<IMapMarker> {
        const marker = new this._mapboxgl.Marker({
            element: this._getMarkerContent(options?.iconUrl ?? "", options?.scale ?? 1),
            anchor: "bottom"
        })
            .setLngLat([coords.lng, coords.lat])
            .addTo(this._map);

        const handle = new MapboxMarker(marker);
        this._markers.push(handle);

        if (options?.center) {
            this.setCenter(coords);
            if (typeof options.zoom === "number")
                this.setZoom(options.zoom);
        }

        return handle;
    }

    removeMarker(marker: IMapMarker): void {
        if (!marker) return;

        marker.remove();
        this._markers = this._markers.filter(m => m !== marker);

        if (marker === this._popupAnchor)
            this.closeInfo();
    }

    clearMarkers(): void {
        this._markers.forEach(m => m.remove());
        this._markers = [];
        this.closeInfo();
    }

    async drawRouteAsync(origin: LatLng, destination: LatLng, mode?: RouteLineMode): Promise<void> {
        this.clearRoute();
        const version = this._routeVersion;

        if (mode === "straight")
            await this._drawRouteLineAsync([origin, destination], version);
        else if (mode === "route")
            await this._drawTrueRouteAsync(origin, destination, version);
    }

    clearRoute(): void {
        // Supersedes any draw still waiting on Directions or the style, so it can't land afterwards.
        this._routeVersion++;
        this._routeAbortController?.abort();
        this._routeAbortController = null;

        if (this._map.getLayer(ROUTE_LAYER_ID))
            this._map.removeLayer(ROUTE_LAYER_ID);

        if (this._map.getSource(ROUTE_SOURCE_ID))
            this._map.removeSource(ROUTE_SOURCE_ID);
    }

    openInfo(anchor: IMapMarker, html: string | HTMLElement): void {
        const content = document.createElement("div");
        if (typeof html === "string")
            content.innerHTML = html;
        else
            content.appendChild(html);

        const { lat, lng } = anchor.getPosition();

        this._popup.remove();
        this._popup
            .setLngLat([lng, lat])
            .setDOMContent(content)
            .addTo(this._map);
        this._popupAnchor = anchor;
    }

    closeInfo(): void {
        this._popup.remove();
        this._popupAnchor = null;
    }

    setCenter(coords: LatLng): void {
        this._map.setCenter([coords.lng, coords.lat]);
    }

    getZoom(): number {
        return fromMapboxZoom(this._map.getZoom());
    }

    setZoom(zoom: number): void {
        this._map.setZoom(toMapboxZoom(zoom));
    }

    isInView(coords: LatLng): boolean {
        return this._map.getBounds()?.contains([coords.lng, coords.lat]) ?? false;
    }

    resize(): void {
        this._map.resize();
    }

    setClusterMarkers(): void {
        // Not implemented for Mapbox, same as HERE.
    }

    getBoundsRect(): BoundsRect | null {
        const bounds = this._map.getBounds();
        if (!bounds) return null;

        return {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
        };
    }

    fitBoundsRect(bounds: BoundsRect): void {
        this._map.fitBounds(
            [[bounds.west, bounds.south], [bounds.east, bounds.north]],
            { padding: 40, animate: false }
        );
    }

    onIdle(handler: () => void): void {
        if (this._idleHandler)
            this._map.off("idle", this._idleHandler);

        this._idleHandler = () => handler();
        this._map.on("idle", this._idleHandler);
    }

    onBoundsChanged(handler: () => void): () => void {
        if (this._boundsHandler)
            this._map.off("move", this._boundsHandler);

        const wrapped = () => handler();
        this._boundsHandler = wrapped;
        this._map.on("move", wrapped);

        return () => {
            this._map.off("move", wrapped);
            if (this._boundsHandler === wrapped)
                this._boundsHandler = null;
        };
    }

    private _getMarkerContent(url: string, scale: number): HTMLElement {
        const size = `${Math.max(1, Math.round(30 * scale))}px`;
        const img = document.createElement("img");
        img.src = url;
        img.style.width = size;
        img.style.height = size;
        img.style.cursor = "pointer";
        img.alt = "location icon";
        return img;
    }

    private async _drawTrueRouteAsync(origin: LatLng, destination: LatLng, version: number): Promise<void> {
        const abortController = new AbortController();
        this._routeAbortController = abortController;

        try {
            const request = this._fetchDrivingRouteAsync(origin, destination, abortController.signal);
            const path = await failIfRouteUnansweredAsync(request);
            await this._drawRouteLineAsync(path, version);
        } catch (error) {
            if (abortController.signal.aborted)
                return;

            abortController.abort();
            throw error;
        }
    }

    private async _fetchDrivingRouteAsync(origin: LatLng, destination: LatLng, signal: AbortSignal): Promise<LatLng[]> {
        const coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}`
            + `?geometries=geojson&overview=full&access_token=${encodeURIComponent(this._apiKey)}`;

        const response = await fetch(url, { signal });
        if (!response.ok)
            throw new Error(`Mapbox Directions request failed with status ${response.status}.`);

        const result = await response.json();
        const routeCoordinates: [number, number][] | undefined = result?.routes?.[0]?.geometry?.coordinates;
        if (!routeCoordinates?.length)
            throw new Error("Mapbox Directions found no drivable route.");

        return routeCoordinates.map(([lng, lat]) => ({ lat, lng }));
    }

    private async _drawRouteLineAsync(path: LatLng[], version: number): Promise<void> {
        await this._styleLoaded;
        if (version !== this._routeVersion) return;

        const data: GeoJSON.Feature<GeoJSON.LineString> = {
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: path.map(p => [p.lng, p.lat]) }
        };

        this._map.addSource(ROUTE_SOURCE_ID, { type: "geojson", data });
        this._map.addLayer({
            id: ROUTE_LAYER_ID,
            type: "line",
            source: ROUTE_SOURCE_ID,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": "#ED1C24", "line-width": 4 }
        });
    }

}
